import { useGetDashboardQuery } from 'store/services/core/dashboard';
import { useGetEmailForBugReportQuery, useGetUISettingsQuery, useGetSettingsQuery } from 'store/services/core/settings';
import i18n from 'i18next';

import type { ReactNode } from 'react';
import { Languages } from 'types/translator';
import type { ColorType } from 'types';

import { memo, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { setColors, setEmail, setOverview } from 'store/entities/dashboard/actions';
import { useLogin } from 'contexts';
import { useStorage } from 'contexts/index';
import { STORAGE_KEYS } from 'types/storage';
import { formatDashboardData } from 'utils/dashboard';

export const Prefetch = memo(({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) => {
  const dispatch = useDispatch();
  const { user } = useLogin();
  const [isLanguageSet, setIsLanguageSet] = useState(false);
  const { getItem, setItem } = useStorage();

  const shouldSkip = !user || !isLanguageSet;

  const { data: uiData, isLoading: isUILoading, error: uiError } = useGetUISettingsQuery(undefined, { skip: shouldSkip });

  const { data: emailData, isLoading: isEmailLoading } = useGetEmailForBugReportQuery(undefined, { skip: shouldSkip });

  useGetSettingsQuery(undefined, { skip: shouldSkip });

  const { data: dashboardData } = useGetDashboardQuery(undefined, { skip: !user });

  const colors = useMemo(
    () =>
      uiData?.uiSettings.reduce(
        (acc, { key, value }) => {
          if (key !== 'dashboard') {
            acc[key as ColorType] = value;
          }
          return acc;
        },
        {} as Record<ColorType, string>
      ),
    [uiData]
  );

  const overview = useMemo(() => {
    const dashboardValue = uiData?.uiSettings.find((setting) => setting.key === 'dashboard')?.value;

    return dashboardData ? formatDashboardData(dashboardData.data) : dashboardValue ? JSON.parse(dashboardValue) : [];
  }, [dashboardData, uiData?.uiSettings]);

  useEffect(() => {
    let isMounted = true;

    const setupLanguage = async () => {
      const locale = getItem(STORAGE_KEYS.LANGUAGE);
      const supportedLanguages = [Languages.CS, Languages.EN, Languages.SK];

      if (locale?.language && supportedLanguages.includes(locale.language)) {
        await i18n.changeLanguage(locale.language);
      } else {
        const baseLanguage = (i18n.resolvedLanguage ?? i18n.language).split('-')[0];
        const selectedLanguage = supportedLanguages.find((language) => language === baseLanguage) ?? Languages.CS;

        await i18n.changeLanguage(selectedLanguage);
        setItem(STORAGE_KEYS.LANGUAGE, { language: selectedLanguage });
      }

      if (isMounted) {
        setIsLanguageSet(true);
      }
    };

    void setupLanguage();

    return () => {
      isMounted = false;
    };
  }, [getItem, setItem]);

  useEffect(() => {
    if (uiError || isUILoading || isEmailLoading) return;

    if (emailData) {
      dispatch(setEmail(emailData.email));
    }

    if (colors) {
      dispatch(setColors(colors));
    }

    if (overview) {
      dispatch(setOverview(overview));
    }
  }, [colors, dispatch, emailData, isEmailLoading, isUILoading, overview, uiData, uiError]);

  const loading = isUILoading || isEmailLoading;

  return loading ? fallback : children;
});
